import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Shield, Target, TrendingDown, GitBranch, AlertTriangle } from 'lucide-react'
import { advancedOrdersApi, marketsApi } from '../lib/api'
import { cn } from '../lib/utils'

type OrderType = 'STOP_LOSS' | 'TAKE_PROFIT' | 'TRAILING_STOP' | 'BRACKET'

interface AdvancedOrderModalProps {
  onClose: () => void
}

const orderTypes = [
  {
    type: 'STOP_LOSS' as OrderType,
    label: 'Stop Loss',
    description: 'Sell when price drops to limit losses',
    icon: Shield,
    color: 'red',
  },
  {
    type: 'TAKE_PROFIT' as OrderType,
    label: 'Take Profit',
    description: 'Sell when price hits target to lock gains',
    icon: Target,
    color: 'green',
  },
  {
    type: 'TRAILING_STOP' as OrderType,
    label: 'Trailing Stop',
    description: 'Dynamic stop that follows price up',
    icon: TrendingDown,
    color: 'orange',
  },
  {
    type: 'BRACKET' as OrderType,
    label: 'Bracket Order',
    description: 'Entry with both stop loss and take profit',
    icon: GitBranch,
    color: 'blue',
  },
]

export function AdvancedOrderModal({ onClose }: AdvancedOrderModalProps) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedType, setSelectedType] = useState<OrderType>('STOP_LOSS')
  
  // Form state
  const [marketId, setMarketId] = useState('')
  const [side, setSide] = useState<'YES' | 'NO'>('YES')
  const [size, setSize] = useState('')
  const [entryPrice, setEntryPrice] = useState('')
  const [stopLossPrice, setStopLossPrice] = useState('')
  const [stopLossPercent, setStopLossPercent] = useState('')
  const [takeProfitPrice, setTakeProfitPrice] = useState('')
  const [takeProfitPercent, setTakeProfitPercent] = useState('')
  const [trailingDistance, setTrailingDistance] = useState('')
  const [trailingPercent, setTrailingPercent] = useState(false)

  const { data: markets } = useQuery({
    queryKey: ['markets'],
    queryFn: () => marketsApi.getAll().then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: () => advancedOrdersApi.create({
      marketId,
      side,
      size: parseFloat(size),
      type: selectedType,
      entryPrice: entryPrice ? parseFloat(entryPrice) : undefined,
      stopLossPrice: stopLossPrice ? parseFloat(stopLossPrice) : undefined,
      stopLossPercent: stopLossPercent ? parseFloat(stopLossPercent) : undefined,
      takeProfitPrice: takeProfitPrice ? parseFloat(takeProfitPrice) : undefined,
      takeProfitPercent: takeProfitPercent ? parseFloat(takeProfitPercent) : undefined,
      trailingDistance: trailingDistance ? parseFloat(trailingDistance) : undefined,
      trailingPercent,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advancedOrders'] })
      onClose()
    },
  })

  const selectedMarket = markets?.markets?.find((m: any) => m.id === marketId)

  const canProceed = () => {
    if (!marketId || !size) return false
    
    switch (selectedType) {
      case 'STOP_LOSS':
        return stopLossPrice || stopLossPercent
      case 'TAKE_PROFIT':
        return takeProfitPrice || takeProfitPercent
      case 'TRAILING_STOP':
        return trailingDistance
      case 'BRACKET':
        return (stopLossPrice || stopLossPercent) && (takeProfitPrice || takeProfitPercent)
      default:
        return false
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Create Advanced Order</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {step === 1 ? (
            <>
              <p className="text-gray-600">Select the type of advanced order you want to create:</p>

              <div className="grid grid-cols-1 gap-3">
                {orderTypes.map((type) => {
                  const Icon = type.icon
                  const isSelected = selectedType === type.type

                  return (
                    <button
                      key={type.type}
                      type="button"
                      onClick={() => setSelectedType(type.type)}
                      className={cn(
                        'flex items-start p-4 rounded-lg border-2 text-left transition-colors',
                        isSelected
                          ? `border-${type.color}-500 bg-${type.color}-50`
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <div className={cn(
                        'p-2 rounded-lg mr-4',
                        `bg-${type.color}-100`
                      )}>
                        <Icon className={cn('w-5 h-5', `text-${type.color}-600`)} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{type.label}</p>
                        <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                      </div>
                    </button>
                  )
                })}
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                >
                  Continue
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-sm text-blue-600 hover:text-blue-700 mb-4"
              >
                ← Back to order type
              </button>

              {/* Market Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Market</label>
                <select
                  value={marketId}
                  onChange={(e) => setMarketId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select a market...</option>
                  {markets?.markets?.map((market: any) => (
                    <option key={market.id} value={market.id}>
                      {market.question}
                    </option>
                  ))}
                </select>
              </div>

              {/* Side Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSide('YES')}
                    className={cn(
                      'flex items-center justify-center px-4 py-3 rounded-lg border-2 font-medium transition-colors',
                      side === 'YES'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    Buy YES
                  </button>
                  <button
                    type="button"
                    onClick={() => setSide('NO')}
                    className={cn(
                      'flex items-center justify-center px-4 py-3 rounded-lg border-2 font-medium transition-colors',
                      side === 'NO'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    Buy NO
                  </button>
                </div>
              </div>

              {/* Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Size (Shares)</label>
                <input
                  type="number"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Entry Price (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Price (optional)
                </label>
                <input
                  type="number"
                  value={entryPrice}
                  onChange={(e) => setEntryPrice(e.target.value)}
                  placeholder="Market order if empty"
                  min="0.01"
                  max="0.99"
                  step="0.001"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {selectedMarket && (
                  <p className="text-xs text-gray-500 mt-1">
                    Current: YES {(selectedMarket.yesPrice * 100).toFixed(1)}¢ / 
                    NO {(selectedMarket.noPrice * 100).toFixed(1)}¢
                  </p>
                )}
              </div>

              {/* Stop Loss */}
              {(selectedType === 'STOP_LOSS' || selectedType === 'BRACKET') && (
                <div className="space-y-3">
                  <p className="font-medium text-gray-900">Stop Loss</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Price</label>
                      <input
                        type="number"
                        value={stopLossPrice}
                        onChange={(e) => {
                          setStopLossPrice(e.target.value)
                          setStopLossPercent('')
                        }}
                        placeholder="0.50"
                        min="0.01"
                        max="0.99"
                        step="0.001"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Or %</label>
                      <input
                        type="number"
                        value={stopLossPercent}
                        onChange={(e) => {
                          setStopLossPercent(e.target.value)
                          setStopLossPrice('')
                        }}
                        placeholder="10"
                        min="0.1"
                        max="100"
                        step="0.1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Take Profit */}
              {(selectedType === 'TAKE_PROFIT' || selectedType === 'BRACKET') && (
                <div className="space-y-3">
                  <p className="font-medium text-gray-900">Take Profit</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Price</label>
                      <input
                        type="number"
                        value={takeProfitPrice}
                        onChange={(e) => {
                          setTakeProfitPrice(e.target.value)
                          setTakeProfitPercent('')
                        }}
                        placeholder="0.75"
                        min="0.01"
                        max="0.99"
                        step="0.001"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Or %</label>
                      <input
                        type="number"
                        value={takeProfitPercent}
                        onChange={(e) => {
                          setTakeProfitPercent(e.target.value)
                          setTakeProfitPrice('')
                        }}
                        placeholder="25"
                        min="0.1"
                        max="1000"
                        step="0.1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Trailing Stop */}
              {selectedType === 'TRAILING_STOP' && (
                <div className="space-y-3">
                  <p className="font-medium text-gray-900">Trailing Distance</p>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      value={trailingDistance}
                      onChange={(e) => setTrailingDistance(e.target.value)}
                      placeholder="0.05"
                      min="0.001"
                      step="0.001"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={trailingPercent}
                        onChange={(e) => setTrailingPercent(e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">%</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    {trailingPercent 
                      ? 'Stop will trail by this percentage below the highest price'
                      : 'Stop will trail by this absolute price distance'}
                  </p>
                </div>
              )}

              {/* Error */}
              {createMutation.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">
                    {(createMutation.error as any)?.response?.data?.error || 'Failed to create order'}
                  </p>
                </div>
              )}

              {/* Submit */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={!canProceed() || createMutation.isPending}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Order'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  )
}
